// deps
import React, { ReactElement, memo, useContext, useMemo, useState, useCallback } from "react";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

// components
import CommonHeaderContent from "@payment-client/components/common-header-content/CommonHeaderContent";
import CommonUIWrapper from "@payment-client/components/common-ui-wrapper/CommonUIWrapper";
import { DeliveryAddressView, CvvBottomSheet } from "@swiggy-private/payment-ui";
import { SuperButton } from "@swiggy-private/dls-web-core/dist/components/SuperButton";

// defs
import type { FetchBillDetailsResponse } from "@payment-client/interfaces/Checkout";

// widgets
import { BillDetails } from "./widgets/BillDetails";
import { EMIDetails } from "./widgets/EMIDetails";
import LoadingScreen from "../LoadingScreen";

// context
import { ShowPaymentContext } from "@payment-client/contexts/ShowPaymentContext";
import { EmiSelectionContext } from "@payment-client/contexts/EmiSelectionContext";
import { PlaceOrderContext } from "@payment-client/contexts/PlaceOrderContext";
import {
    AnalyticsContext,
    CheckoutRoute,
    DeliveryInfoContext,
    isEmpty,
    useNavigate,
} from "@swiggy-private/checkout-core";

// helper
import { getPaymentHeaderSubtitleV4 } from "@payment-client/helpers/PaymentHelper";
import { getDataForDeliveryAddressViewV4 } from "@payment-client/helpers/DeliveryAddressViewHelper";
import {
    getPaymentMethod,
    isCvvMandatory,
} from "@payment-client/components/show-payments/components/review-bill-details-renderer/helpers";
import { getNewcardPaymentMethod } from "@payment-client/helpers/CardHelper";
import { clickProceedToPay } from "@payment-client/components/show-payments/components/review-bill-details-renderer/helpers/analytics";
import { None } from "@swiggy-private/js-fp";
import { format } from "@swiggy-private/js-utils";

// hooks
import { useOnPay } from "@payment-client/hooks/UseOnPay";

// styled components
import { ContentContainer, WidgetCard, Footer } from "./styled";

// constants
import { EMI_SUPPORTED_GATEWAY_ID } from "@swiggy-private/payment-ui";

interface IProps {
    data: FetchBillDetailsResponse | null;
    loading: boolean;
}

export const TEXTS = {
    PROCEED_TO_ADD_NEW_CARD: "Proceed to add new card",
    PLACE_ORDER: "Pay \u{20B9}{0}",
    REVIEW_PLAN_AND_BILL_DETAILS: "Review Plan & Bill Details",
    PAYMENT_METHOD_NOT_FOUND: "Payment method not found",
    TITLE: "Review Bill Details",
};

function Content({ data, loading }: IProps): ReactElement | null {
    const { clickEvent } = useContext(AnalyticsContext);
    const { placeOrderInProgress } = useContext(PlaceOrderContext);
    const { paymentResponse, checkoutMetaInfo } = useContext(ShowPaymentContext);
    const { deliverySelectedSlot } = useContext(DeliveryInfoContext);
    const { selectedEmiDetails } = useContext(EmiSelectionContext);
    const [showCvvPopup, setShowCvvPopup] = useState(false);
    const onPay = useOnPay();
    const navigateTo = useNavigate();

    const { presentationDetails, itemQuantity, offerBanner } = paymentResponse || {};
    const [bill, emi] = data?.widgets || [];

    const deliveryAddressViewData = getDataForDeliveryAddressViewV4({
        checkoutMetaInfo,
        presentationDetails,
        deliverySelectedSlot,
    });

    const paymentMethod = useMemo(() => {
        if (!isEmpty(selectedEmiDetails?.cardReference)) {
            return getPaymentMethod({
                cardReference: selectedEmiDetails?.cardReference,
                paymentResponse,
            });
        }

        if (!paymentResponse?.paymentGroup) {
            return None;
        }

        return getNewcardPaymentMethod(paymentResponse?.paymentGroup);
    }, [selectedEmiDetails, paymentResponse]);

    const handlePlaceOrder = useCallback(
        (cvv?: string) => {
            setShowCvvPopup(false);

            if (!data || !selectedEmiDetails) {
                return;
            }

            if (placeOrderInProgress) {
                return;
            }

            const { tenure: emiTenure, emiBank, emiType, txnEmiType, cardReference, bankCode } = selectedEmiDetails;
            const extraEmiParams = {
                emiBank,
                bankCode,
                emiType,
                txnEmiType,
                isEmi: true,
                supportedGatewayId: EMI_SUPPORTED_GATEWAY_ID,
                emiTenure,
                cardReference,
                cvv: cvv || "",
            };

            if (paymentMethod.isNone()) {
                __DEV__ && console.error("Payment method not found");
                return;
            }

            const paymentMethodData = paymentMethod.unwrap();
            onPay(
                {
                    ...paymentMethodData,
                    payableAmount: String(data.finalToPay),
                },
                extraEmiParams,
            );
        },
        [data, paymentMethod, selectedEmiDetails, onPay, placeOrderInProgress],
    );

    const doPlaceOrder = useCallback(() => {
        clickProceedToPay(clickEvent);

        const cvvNeeded = isCvvMandatory({
            cardReference: selectedEmiDetails?.cardReference,
            paymentResponse,
        });

        if (cvvNeeded) {
            setShowCvvPopup(true);
        } else {
            handlePlaceOrder();
        }
    }, [clickEvent, paymentResponse, selectedEmiDetails, handlePlaceOrder]);

    const handleClick = useCallback(() => {
        if (!selectedEmiDetails?.cardReference) {
            navigateTo(CheckoutRoute.ADD_NEW_CARD, {
                paymentMethod: paymentMethod.unwrap(),
            });
        } else {
            doPlaceOrder();
        }
    }, [selectedEmiDetails, paymentMethod, navigateTo, doPlaceOrder]);

    const buttonText = useMemo(() => {
        if (!selectedEmiDetails?.cardReference) {
            return TEXTS.PROCEED_TO_ADD_NEW_CARD;
        } else if (data) {
            return format(TEXTS.PLACE_ORDER, String(data.finalToPay));
        }
    }, [selectedEmiDetails?.cardReference, data]);

    const content = useMemo(
        () => (
            <>
                {deliveryAddressViewData != null && (
                    <DeliveryAddressView {...deliveryAddressViewData} couponBannerVisible={!isEmpty(offerBanner)} />
                )}
                <WidgetCard>
                    <Text variant="V2.body1.neutral.bold">{TEXTS.TITLE}</Text>
                    {loading || !data ? (
                        <LoadingScreen />
                    ) : (
                        <ContentContainer>
                            {bill && <BillDetails bill={bill} />}
                            {emi && <EMIDetails emi={emi} />}
                        </ContentContainer>
                    )}
                </WidgetCard>
                <Footer>
                    <SuperButton
                        onClick={handleClick}
                        buttonType="specialPositive"
                        buttonSize="large"
                        loading={placeOrderInProgress || isEmpty(data)}
                    >
                        {buttonText}
                    </SuperButton>
                </Footer>
                {showCvvPopup && paymentMethod.isSome() && (
                    <CvvBottomSheet
                        methodData={paymentMethod.unwrap()}
                        onCloseCvvBs={() => setShowCvvPopup(false)}
                        onCardPayAction={handlePlaceOrder}
                    />
                )}
            </>
        ),
        [
            deliveryAddressViewData,
            offerBanner,
            loading,
            data,
            bill,
            emi,
            handleClick,
            placeOrderInProgress,
            buttonText,
            showCvvPopup,
            paymentMethod,
            handlePlaceOrder,
        ],
    );

    const header = useMemo(() => {
        const subtitle = getPaymentHeaderSubtitleV4({
            itemCount: Number(itemQuantity),
            amount: String(data?.finalToPay || "-"),
            paymentResponse,
        });

        return (
            <CommonHeaderContent
                quantity={Number(itemQuantity)}
                title={TEXTS.REVIEW_PLAN_AND_BILL_DETAILS}
                grayscale={false}
                subtitle={subtitle}
            />
        );
    }, [itemQuantity, data, paymentResponse]);

    return <CommonUIWrapper header={header} content={content} />;
}

export default memo(Content);
